import { HttpService } from 'core/modules/network-service';
import { get } from 'lodash';

// local dependencies
import { SERVICE_RESPONSE_STATUS } from 'constants/common';
import { getVisibleItems, isVisible } from 'resolvers/getVisibleEntities';

/*************************************************************/

/**
 * @type {class}
 * @desc MemberInfoCommunicationSettingsPhone defines an HTTP service interface to send HTTP network requets.
 * @example
 * import { MemberInfoCommunicationSettingsPhoneService } from 'services/memberInfoCommunicationSettingsPhone';
 *
 * // create service instance and send/cancel request
 * const memberInfoCommunicationSettingsPhoneService = new MemberInfoCommunicationSettingsPhoneService();
 * const result = await memberInfoCommunicationSettingsPhoneService.fetchData( '<user-id>' ); // fetch data
 * memberInfoCommunicationSettingsPhoneService.cancel(); // cancel an active request
 */
export class MemberInfoCommunicationSettingsPhoneService extends HttpService {

    // make request and fetch data
    async fetchData( { url = {}, data = {} } = {} ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data,
            } );

            // extract component data
            const visibility = get( response, 'body.component.visibility', {} );
            const contacts = get( response, 'body.component.data.state.contacts', [] );
            const addAction = get( response, 'body.component.structure.addAction', {} );
            const modifyActions = get( response, 'body.component.structure.modifyActions', [] );
            const communicationPreferenceAction = get( response, 'body.component.structure.communicationPreferenceAction', {} );
            const communicationPreferenceFlag = get( response, 'body.component.data.state.communicationPreferenceFlag', [] );
            const memberChannelPreferences = get( response, 'body.component.data.state.memberChannelPreferences', [] );
            const updateMemberPreferencesRequest = get( response, 'body.component.data.state.updateMemberPreferencesRequest', [] );

          
            if( SERVICE_RESPONSE_STATUS.SUCCESS === get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ) ) {
                return {
                    status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.ERROR ),
                    data: {
                        contacts: contacts,
                        communicationPreferenceFlag,
                        memberChannelPreferences,
                        updateMemberPreferencesRequest,
                        options: get( response, 'body.component.data.options', {} ),
                        addAction: isVisible( addAction, visibility ) ? addAction : {},
                        modifyActions: getVisibleItems( modifyActions, visibility ),
                        communicationPreferenceAction: isVisible( communicationPreferenceAction, visibility ) ? communicationPreferenceAction : {}
                    },
                };
            } else {
                return {
                    status: SERVICE_RESPONSE_STATUS.FAILURE,
                    data: get( response, 'body.component.data', {} )
                };
            }
        } catch( errResponse ) {
            return errResponse;
        }
    }

    // delete a contact
    async deleteItem( { url = {}, data = {} } = {} ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data
            } );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: get( response, 'body.component.data.state', get( response, 'body.component.data', {} ) ),
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // make given contact as default one
    async defaultItem( { url = {}, data = {} } = {} ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data
            } );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: get( response, 'body.component.data.state', get( response, 'body.component.data', {} ) ),
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // update a contact
    async addUpdatePhoneNumber( { url = {}, data = {} } = {} ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data
            } );
    
            return {
                _status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: get( response, 'body.component.data.state', get( response, 'body.component.data', {} ) )
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // set communication Preference
    async setCommunicationPreference( { url = {}, data = {} } = {} ) {
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data
            } );
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: get( response, 'body.component.data.state', get( response, 'body.component.data', {} ) ),
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }
}
